import { type ControlScheme, TUNABLES } from "./config";
import {
  type BountyRules,
  type CityCommandRules,
  type CompanyRules,
  FACTION_IDS,
  type FactionId,
  type FindRules,
  hashString,
  type RunOptions,
  type SoldierRules,
  seatPlaces,
  type TacticsRules,
  without,
} from "./core";
import { cleanRoomCode } from "./net/roomCode";
import type { ModeName, SeatName } from "./render/seats";

/**
 * How a run is set up from the address and the date, shared by the front page (B3.1), which
 * draws the day's map before the game loads, and by the game itself, so both read the same map.
 * No Pixi here: the front page imports it.
 */

/** A duel's code in the address (`?room=`, B3.2), cleaned; undefined without a valid one. */
export function roomOfQuery(search: string): string | undefined {
  return cleanRoomCode(new URLSearchParams(search).get("room") ?? "");
}

/**
 * Daily seed is the UTC calendar date hashed outside core (determinism rule).
 * `?seed=<text>` overrides it so the smoke test and bug reports are reproducible, and a duel's
 * code in `?room=` (B3.2) overrides both: the room is the seed (D-028).
 */
export function seedFor(
  search: string,
  now: Date,
): { readonly seedText: string; readonly seed: number; readonly daily: boolean } {
  const room = roomOfQuery(search);
  if (room !== undefined) {
    return { seedText: room, seed: hashString(room), daily: false };
  }
  const override = new URLSearchParams(search).get("seed");
  const daily = !(override && override.length > 0);
  const seedText = override && !daily ? override : now.toISOString().slice(0, 10);
  return { seedText, seed: hashString(seedText), daily };
}

/**
 * The seat a new run is played from (ADR-0014 M1): the King, or one Spear of the player's
 * kingdom under a scripted King. Both are in every build: the start screen asks (5.18), and
 * `?seat=king` or `?seat=soldier` names the seat in the address, like `?seed`, and starts
 * without asking. A saved run keeps the seat it was started with. The soldier's run has the
 * finds under the land and the purse (M2) and a King who pays for its work (C1) and the rules
 * of company (C2); the King's has none of them. The King's seat with the `"cities"` controls
 * carries City Command (CC1); with `"classic"` it carries no field of it, so its bytes do not
 * move. A soldier's run never does. FL2: the shipped tactics carry luck (D-090); `luck` false
 * (`?luck=off`) carries them without it, on either seat; otherwise the seat carries no tactics of
 * its own.
 */
export interface SeatRules {
  readonly soldier: SoldierRules | null;
  readonly finds: FindRules | null;
  readonly bounty: BountyRules | null;
  readonly company: CompanyRules | null;
  readonly cityCommand?: CityCommandRules;
  readonly tactics?: TacticsRules;
}

export function seatRulesOf(
  seat: SeatName,
  scheme: ControlScheme = TUNABLES.controls.scheme,
  valor?: boolean,
  luck?: boolean,
): SeatRules {
  const lucky = luck === false ? { tactics: without(TUNABLES.rules.tactics, "luck") } : {};
  if (seat === "soldier") {
    return {
      soldier: soldierWith(duelSoldier(), valor),
      finds: TUNABLES.finds,
      bounty: TUNABLES.bounty,
      company: TUNABLES.company,
      ...lucky,
    };
  }
  const king: SeatRules = { soldier: null, finds: null, bounty: null, company: null, ...lucky };
  return scheme === "cities" ? { ...king, cityCommand: TUNABLES.cityCommand } : king;
}

/**
 * The duel's soldier seat as it ships: with valor when `TUNABLES.soldierDuelValor` says so
 * (D-129, off since N53, D-130), never with Command and head money (G4, D-088). The company's
 * soldier seats keep `soldierSeat` alone.
 */
function duelSoldier(): SoldierRules {
  return TUNABLES.soldierDuelValor
    ? { ...TUNABLES.soldierSeat, valor: TUNABLES.soldierValor }
    : TUNABLES.soldierSeat;
}

/**
 * The soldier's seat with valor (V1) and its coins (VC) as the address asks: `true` spreads
 * `TUNABLES.soldierValor` and `TUNABLES.soldierCoins` in, `false` takes both out, undefined keeps
 * the seat as it ships.
 */
function soldierWith(soldier: SoldierRules, valor: boolean | undefined): SoldierRules {
  if (valor === undefined) {
    return soldier;
  }
  const { valor: _valor, coins: _coins, ...plain } = soldier;
  return valor ? { ...plain, valor: TUNABLES.soldierValor, coins: TUNABLES.soldierCoins } : plain;
}

/**
 * The company duel's rules (K1): the King's City Command and the soldier's blocks in one rulebook,
 * every kingdom with `TUNABLES.companyMode.seats`; the same whichever seat the human holds
 * (parity), so the seat is only which one is held (`companyOptionsFor`). Valor and luck as the
 * address asks, as on the soldier's seat.
 */
export function companyRulesOf(valor?: boolean, luck?: boolean): SeatRules {
  const lucky = luck === false ? { tactics: without(TUNABLES.rules.tactics, "luck") } : {};
  return {
    soldier: { ...soldierWith(TUNABLES.soldierSeat, valor), seats: TUNABLES.companyMode.seats },
    finds: TUNABLES.finds,
    bounty: TUNABLES.bounty,
    company: TUNABLES.company,
    cityCommand: TUNABLES.cityCommand,
    ...lucky,
  };
}

/**
 * Dev-only URL knobs for walking the whole loop quickly: `?hexes=20&run=90&speed=4`.
 * `?court=0` plays without the King (the Increment 4 rules), for comparing the two games.
 * `?king=enemy` plays the other kingdom's King (B3.3): the run starts with that King's seat held.
 */
export interface DevKnobs {
  readonly court: boolean;
  readonly hexCount: number | undefined;
  /** Seconds to the bell; undefined keeps the mode's (K7: the war's is its own). */
  readonly runLengthSec: number | undefined;
  /** Seconds until sudden death (A57); undefined keeps the shipped tick. */
  readonly suddenDeathSec: number | undefined;
  readonly speed: number;
  /** The kingdom whose King the human holds (B3.3); undefined keeps the player's. */
  readonly king: FactionId | undefined;
}

function positiveIntParam(params: URLSearchParams, name: string): number | undefined {
  const raw = params.get(name);
  const value = raw === null ? Number.NaN : Number.parseInt(raw, 10);
  return Number.isInteger(value) && value > 0 ? value : undefined;
}

export function devKnobsFor(search: string, devBuild: boolean): DevKnobs {
  const defaults: DevKnobs = {
    court: true,
    hexCount: undefined,
    runLengthSec: undefined,
    suddenDeathSec: undefined,
    speed: 1,
    king: undefined,
  };
  if (!devBuild) {
    return defaults;
  }
  const params = new URLSearchParams(search);
  const speed = positiveIntParam(params, "speed");
  const allowedSpeeds: readonly number[] = TUNABLES.dev.speeds;
  return {
    court: params.get("court") !== "0",
    hexCount: positiveIntParam(params, "hexes"),
    runLengthSec: positiveIntParam(params, "run"),
    suddenDeathSec: positiveIntParam(params, "sudden"),
    speed: speed !== undefined && allowedSpeeds.includes(speed) ? speed : defaults.speed,
    king: FACTION_IDS.find((faction) => faction === params.get("king")),
  };
}

/** The smallest map radius whose disc (3r^2 + 3r + 1 hexes) leaves the blob the slack the shipped map has. */
function radiusFor(hexCount: number): number {
  let radius: number = TUNABLES.mapMaxRadius;
  while (3 * radius * radius + 3 * radius + 1 < hexCount * TUNABLES.dev.bigMapDiscSlack) {
    radius += 1;
  }
  return radius;
}

/** A mode's bell and sudden death, in ticks: the duel's, or the war's own (K7); the dev knobs turn both. */
export interface ModeClock {
  readonly runLengthTicks: number;
  readonly suddenDeathTick: number;
}

const DUEL_CLOCK: ModeClock = {
  runLengthTicks: TUNABLES.runLengthSec * TUNABLES.ticksPerSecond,
  suddenDeathTick: TUNABLES.rules.suddenDeathTick,
};

export function runOptionsFor(
  knobs: DevKnobs,
  seat: SeatRules,
  hexCount: { readonly min: number; readonly max: number } = TUNABLES.mapHexCount,
  clock: ModeClock = DUEL_CLOCK,
): RunOptions {
  const options: RunOptions = {
    map: {
      minHexCount: knobs.hexCount ?? hexCount.min,
      maxHexCount: knobs.hexCount ?? hexCount.max,
      maxRadius: radiusFor(knobs.hexCount ?? hexCount.max),
      noiseScale: TUNABLES.mapShape.noiseScale,
      shapeNoiseWeight: TUNABLES.mapShape.noiseWeight,
    },
    rules: {
      ...TUNABLES.rules,
      runLengthTicks:
        knobs.runLengthSec === undefined
          ? clock.runLengthTicks
          : knobs.runLengthSec * TUNABLES.ticksPerSecond,
      suddenDeathTick:
        knobs.suddenDeathSec === undefined
          ? clock.suddenDeathTick
          : knobs.suddenDeathSec * TUNABLES.ticksPerSecond,
      court: knobs.court ? TUNABLES.rules.court : null,
      ...seat,
    },
  };
  if (knobs.king === undefined || seat.soldier !== null) {
    return options;
  }
  // B3.3, K0: the knob seats the human at another kingdom's King; without it the run's default.
  const king = seatPlaces(FACTION_IDS, options.rules).findIndex(
    (place) => place.faction === knobs.king && place.role === "king",
  );
  return { ...options, held: [king] };
}

/**
 * The company duel's options (K1): its rules on its own map, one seat held, the one the human
 * asked for: the player's King (seat 0) or the player's first soldier (seat 1); every other is
 * its script's. `?king=` plays no part.
 */
export function companyOptionsFor(
  knobs: DevKnobs,
  seat: SeatName,
  valor?: boolean,
  luck?: boolean,
): RunOptions {
  const options = runOptionsFor(
    { ...knobs, king: undefined },
    companyRulesOf(valor, luck),
    TUNABLES.companyMode.mapHexCount,
  );
  return { ...options, held: [seat === "king" ? 0 : 1] };
}

/**
 * Two against two (K6): the duel's options for the seat on `TUNABLES.alliesMode`'s map, its four
 * kingdoms on its two sides, the starts in its group's orbits (K5) as `TUNABLES.kingdomStarts`.
 */
export function alliesOptionsFor(
  knobs: DevKnobs,
  seat: SeatName,
  scheme?: ControlScheme,
  valor?: boolean,
  luck?: boolean,
): RunOptions {
  const { sides, symmetry, mapHexCount } = TUNABLES.alliesMode;
  const options = runOptionsFor(knobs, seatRulesOf(seat, scheme, valor, luck), mapHexCount);
  return {
    ...options,
    rules: { ...options.rules, starts: { symmetry, ...TUNABLES.kingdomStarts }, sides },
    factionCount: sides.flat().length,
  };
}

/**
 * The war (K7): the duel's options for the seat on `TUNABLES.warMode`'s map, its land and its
 * clock, two sides of ten kingdoms in C2's orbits (K5), and the fall of a capital (ADR-0012 W4).
 */
export function warOptionsFor(
  knobs: DevKnobs,
  seat: SeatName,
  scheme?: ControlScheme,
  valor?: boolean,
  luck?: boolean,
): RunOptions {
  const { sides, symmetry, mapHexCount, land, runLengthSec, suddenDeathSec } = TUNABLES.warMode;
  const options = runOptionsFor(knobs, seatRulesOf(seat, scheme, valor, luck), mapHexCount, {
    runLengthTicks: runLengthSec * TUNABLES.ticksPerSecond,
    suddenDeathTick: suddenDeathSec * TUNABLES.ticksPerSecond,
  });
  return {
    ...options,
    rules: {
      ...options.rules,
      ...land,
      starts: { symmetry, ...TUNABLES.kingdomStarts },
      sides,
      capitalFall: true,
    },
    factionCount: sides.flat().length,
  };
}

/** A run's options by mode and seat: the duel's (`runOptionsFor` over `seatRulesOf`), the company's, two against two's or the war's. */
export function modeOptionsFor(
  knobs: DevKnobs,
  mode: ModeName,
  seat: SeatName,
  scheme?: ControlScheme,
  valor?: boolean,
  luck?: boolean,
): RunOptions {
  if (mode === "company") {
    return companyOptionsFor(knobs, seat, valor, luck);
  }
  if (mode === "war") {
    return warOptionsFor(knobs, seat, scheme, valor, luck);
  }
  return mode === "allies"
    ? alliesOptionsFor(knobs, seat, scheme, valor, luck)
    : runOptionsFor(knobs, seatRulesOf(seat, scheme, valor, luck));
}
